// KycCaseDetailPage.js — Case detail page at /kyc/cases/:caseId
// Core compliance analyst workspace with 12-tab navigation

import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Shield, FileText, Users, AlertTriangle, CheckCircle, Clock, Search, Plus,
  ChevronRight, ChevronDown, Eye, XCircle, BarChart3, Globe, Building2, User,
  ArrowLeft, ArrowRight, RefreshCw, Upload, FileCheck, AlertCircle, Edit3,
  Layers, Activity, DollarSign, Scale, Briefcase, Link2, History, Bell,
  Check, X, UserPlus, ChevronUp, ExternalLink, Copy, MapPin, Flag,
} from 'lucide-react';
import useKycMockData from '../../hooks/useKycMockData';
import KycStatusBadge from '../../components/kyc/KycStatusBadge';
import KycRiskBadge, { KycRiskBar } from '../../components/kyc/KycRiskBadge';
import {
  CASE_TYPE_LABELS, PERSON_ROLE_LABELS, DOC_TYPE_LABELS, ALERT_TYPE_LABELS,
  SCREENING_TYPE_LABELS,
} from '../../lib/kyc/constants';
import { STATE_LABELS, getAvailableTransitions } from '../../lib/kyc/stateMachine';

// ============================================
// SHARED HELPERS
// ============================================

const EmptyState = ({ icon: Icon, title, description, action, onAction }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
      <Icon className="w-7 h-7 text-gray-400" />
    </div>
    <h3 className="text-sm font-semibold text-gray-700 mb-1">{title}</h3>
    <p className="text-xs text-gray-500 max-w-sm mb-3">{description}</p>
    {action && (
      <button onClick={onAction} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">
        <Plus className="w-3 h-3" />
        {action}
      </button>
    )}
  </div>
);

const SectionCard = ({ title, icon: Icon, children, headerAction }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/50">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      </div>
      {headerAction}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const InfoRow = ({ label, value, className = '' }) => (
  <div className={`flex items-center justify-between py-1.5 ${className}`}>
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-xs font-medium text-gray-700">{value || '--'}</span>
  </div>
);

const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '--';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString() : '--';

// ============================================
// TAB CONFIGURATION
// ============================================
const DETAIL_TABS = [
  { key: 'overview',        label: 'Overview',        icon: Layers },
  { key: 'persons',         label: 'Persons',         icon: Users },
  { key: 'ownership',       label: 'Ownership',       icon: Building2 },
  { key: 'screening',       label: 'Screening',       icon: Shield },
  { key: 'risk',            label: 'Risk',            icon: AlertTriangle },
  { key: 'source_of_funds', label: 'Source of Funds', icon: DollarSign },
  { key: 'tax',             label: 'Tax',             icon: Scale },
  { key: 'markets',         label: 'Markets',         icon: Globe },
  { key: 'counterparties',  label: 'Counterparties',  icon: Link2 },
  { key: 'documents',       label: 'Documents',       icon: FileText },
  { key: 'monitoring',      label: 'Monitoring',      icon: Bell },
  { key: 'changes',         label: 'Changes',         icon: History },
];

// ============================================
// OVERVIEW TAB
// ============================================
const OverviewTab = ({ caseData, auditLog, stats }) => {
  const now = new Date();
  const dueDate = caseData.due_date ? new Date(caseData.due_date) : null;
  const daysRemaining = dueDate ? Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Status</p>
          <KycStatusBadge status={caseData.status} size="md" />
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Risk Rating</p>
          {caseData.risk_rating ? (
            <KycRiskBadge rating={caseData.risk_rating} score={caseData.risk_score} showScore size="md" />
          ) : (
            <span className="text-sm text-gray-400">Not assessed</span>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Created</p>
          <p className="text-sm font-semibold text-gray-700">{fmtDate(caseData.created_at)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Last Reviewed</p>
          <p className="text-sm font-semibold text-gray-700">{fmtDate(caseData.last_reviewed)}</p>
        </div>
        <div className={`rounded-xl border p-4 text-center ${daysRemaining !== null && daysRemaining < 0 ? 'bg-red-50 border-red-200' : daysRemaining !== null && daysRemaining <= 3 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
          <p className="text-xs text-gray-500 mb-1">SLA Countdown</p>
          {daysRemaining !== null ? (
            <p className={`text-sm font-bold ${daysRemaining < 0 ? 'text-red-600' : daysRemaining <= 3 ? 'text-amber-600' : 'text-gray-700'}`}>
              {daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue` : `${daysRemaining} days`}
            </p>
          ) : (
            <p className="text-sm text-gray-400">--</p>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-gray-500">Persons</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{caseData.persons_count}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <FileCheck className="w-4 h-4 text-green-500" />
            <span className="text-xs text-gray-500">Docs Verified</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{caseData.docs_verified} / {caseData.docs_total}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Globe className="w-4 h-4 text-indigo-500" />
            <span className="text-xs text-gray-500">Markets</span>
          </div>
          <p className="text-lg font-bold text-gray-900">{caseData.markets?.length || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4 text-purple-500" />
            <span className="text-xs text-gray-500">Screening</span>
          </div>
          <p className="text-sm font-semibold text-gray-900 capitalize">{(caseData.screening_status || 'not_started').replace(/_/g, ' ')}</p>
        </div>
      </div>

      {/* Activity Timeline */}
      <SectionCard title="Recent Activity" icon={Activity}>
        {auditLog.length > 0 ? (
          <div className="space-y-0">
            {auditLog.slice(-10).reverse().map((log, idx) => (
              <div key={log.id} className="flex gap-3 py-2.5 border-b border-gray-50 last:border-0">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5" />
                  {idx < auditLog.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 font-medium">{log.action}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{log.details}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] text-gray-400">{fmtDateTime(log.timestamp)}</span>
                    <span className="text-[10px] text-gray-400">by {log.actor}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4">No activity recorded yet.</p>
        )}
      </SectionCard>
    </div>
  );
};

// ============================================
// PERSONS TAB
// ============================================
const PersonsTab = ({ persons }) => {
  const [expanded, setExpanded] = useState(null);

  const roleColors = {
    applicant:           'bg-blue-100 text-blue-700',
    beneficial_owner:    'bg-purple-100 text-purple-700',
    director:            'bg-indigo-100 text-indigo-700',
    shareholder:         'bg-teal-100 text-teal-700',
    authorized_signatory:'bg-sky-100 text-sky-700',
    trustee:             'bg-amber-100 text-amber-700',
    settlor:             'bg-orange-100 text-orange-700',
    beneficiary:         'bg-green-100 text-green-700',
    fund_manager:        'bg-violet-100 text-violet-700',
  };

  const verificationColors = {
    verified:   'text-green-600',
    pending:    'text-yellow-600',
    not_started:'text-gray-400',
    rejected:   'text-red-600',
  };

  const verificationIcons = {
    verified:   CheckCircle,
    pending:    Clock,
    not_started:AlertCircle,
    rejected:   XCircle,
  };

  if (persons.length === 0) {
    return <EmptyState icon={Users} title="No persons added" description="Add persons associated with this case to begin the KYC process." action="Add Person" />;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500">{persons.length} person{persons.length !== 1 ? 's' : ''}</p>
        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">
          <UserPlus className="w-3.5 h-3.5" />
          Add Person
        </button>
      </div>

      {persons.map((p) => {
        const isExpanded = expanded === p.id;
        const VIcon = verificationIcons[p.verification_status] || AlertCircle;
        return (
          <div key={p.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div
              className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setExpanded(isExpanded ? null : p.id)}
            >
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-gray-900">{p.name}</span>
                  {p.is_primary && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-blue-600 text-white rounded">PRIMARY</span>
                  )}
                  {p.is_pep && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 rounded">PEP</span>
                  )}
                  {p.is_ubo && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded">UBO</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${roleColors[p.role] || 'bg-gray-100 text-gray-600'}`}>
                    {PERSON_ROLE_LABELS[p.role] || p.role}
                  </span>
                  {p.nationality && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {p.nationality}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <VIcon className={`w-4 h-4 ${verificationColors[p.verification_status]}`} />
                {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </div>

            {isExpanded && (
              <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/50">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 mb-4">
                  <InfoRow label="Date of Birth" value={fmtDate(p.date_of_birth)} />
                  <InfoRow label="Email" value={p.email} />
                  <InfoRow label="Verification" value={p.verification_status?.replace(/_/g, ' ')} />
                  <InfoRow label="Screening Status" value={p.screening_status?.replace(/_/g, ' ')} />
                  <InfoRow label="Last Screened" value={fmtDate(p.last_screened)} />
                  <InfoRow label="Nationality" value={p.nationality} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// OWNERSHIP TAB
// ============================================
const OwnershipTab = ({ ownership }) => {
  if (ownership.length === 0) {
    return <EmptyState icon={Building2} title="No ownership structure" description="Add entities and individuals to build the ownership tree." action="Add Entity Layer" />;
  }

  // Build tree from flat array
  const rootNodes = ownership.filter(n => !n.parentId);
  const childrenOf = (parentId) => ownership.filter(n => n.parentId === parentId);
  const ubos = ownership.filter(n => n.type === 'individual');
  const totalRootOwnership = rootNodes.reduce((s, n) => s + (n.ownershipPct || 0), 0);

  // Validation warnings
  const warnings = [];
  const parentGroups = {};
  ownership.forEach(n => {
    const key = n.parentId || '__root__';
    parentGroups[key] = (parentGroups[key] || 0) + (n.ownershipPct || 0);
  });
  Object.entries(parentGroups).forEach(([key, total]) => {
    if (total > 100) {
      warnings.push(`Ownership under ${key === '__root__' ? 'root level' : key} totals ${total}% (exceeds 100%)`);
    }
  });
  if (ubos.length === 0) {
    warnings.push('No individual UBOs identified in the ownership structure');
  }

  const renderNode = (node, depth = 0) => {
    const children = childrenOf(node.id);
    const isUbo = node.type === 'individual' && node.ownershipPct >= 25;
    return (
      <div key={node.id}>
        <div className={`flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 ${depth > 0 ? '' : ''}`}
             style={{ paddingLeft: `${depth * 28 + 12}px` }}>
          {depth > 0 && (
            <div className="w-4 h-px bg-gray-300 flex-shrink-0" />
          )}
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${node.type === 'individual' ? 'bg-blue-100' : 'bg-violet-100'}`}>
            {node.type === 'individual' ? <User className="w-4 h-4 text-blue-600" /> : <Building2 className="w-4 h-4 text-violet-600" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">{node.name}</span>
              {isUbo && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded">UBO</span>
              )}
              {node.isPep && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 rounded">PEP</span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="capitalize">{node.type}</span>
              {node.countryCode && <span>| {node.countryCode}</span>}
            </div>
          </div>
          <span className="text-sm font-bold text-gray-700 flex-shrink-0">{node.ownershipPct}%</span>
        </div>
        {children.map(child => renderNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800 mb-1">Validation Warnings</p>
              {warnings.map((w, i) => (
                <p key={i} className="text-xs text-amber-700">{w}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      <SectionCard
        title="Ownership Structure"
        icon={Building2}
        headerAction={
          <button className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-100">
            <Plus className="w-3 h-3" />
            Add Entity Layer
          </button>
        }
      >
        <div className="space-y-0">
          {rootNodes.map(n => renderNode(n))}
        </div>
        <div className="mt-4 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            UBOs identified: <span className="font-semibold text-gray-700">{ubos.length}</span>
            {' | '}
            Threshold: <span className="font-semibold text-gray-700">25%</span>
          </p>
        </div>
      </SectionCard>
    </div>
  );
};

// ============================================
// SCREENING TAB
// ============================================
const ScreeningTab = ({ persons, screeningHits }) => {
  if (persons.length === 0) {
    return <EmptyState icon={Shield} title="No persons to screen" description="Add persons to this case before running screening." />;
  }

  const hitStatusColors = {
    pending_review: 'bg-yellow-100 text-yellow-700',
    confirmed:      'bg-red-100 text-red-700',
    false_positive: 'bg-gray-100 text-gray-600',
    escalated:      'bg-orange-100 text-orange-700',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{screeningHits.length} hit{screeningHits.length !== 1 ? 's' : ''} across {persons.length} person{persons.length !== 1 ? 's' : ''}</p>
        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">
          <RefreshCw className="w-3.5 h-3.5" />
          Run Screening
        </button>
      </div>

      {persons.map((p) => {
        const personHits = screeningHits.filter(h => h.person_id === p.id);
        return (
          <SectionCard key={p.id} title={p.name} icon={User}>
            <div className="flex items-center gap-3 mb-3 text-xs text-gray-500">
              <span>Status: <span className="font-medium capitalize">{(p.screening_status || 'not_started').replace(/_/g, ' ')}</span></span>
              <span>|</span>
              <span>Last screened: {fmtDate(p.last_screened)}</span>
            </div>

            {personHits.length > 0 ? (
              <div className="space-y-2">
                {personHits.map((hit) => (
                  <div key={hit.id} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">{hit.list_name}</span>
                          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${hitStatusColors[hit.status] || 'bg-gray-100 text-gray-600'}`}>
                            {hit.status === 'pending_review' ? 'Pending Review' : hit.status.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">Matched: {hit.matched_name}</p>
                      </div>
                      <span className={`text-lg font-bold ${hit.match_score >= 80 ? 'text-red-600' : hit.match_score >= 60 ? 'text-amber-600' : 'text-gray-500'}`}>
                        {hit.match_score}%
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">{hit.details}</p>
                    {hit.status === 'pending_review' && (
                      <div className="flex items-center gap-2">
                        <button className="px-2.5 py-1 bg-red-50 text-red-600 text-xs font-medium rounded-lg hover:bg-red-100">
                          Confirm Match
                        </button>
                        <button className="px-2.5 py-1 bg-gray-50 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-100">
                          False Positive
                        </button>
                        <button className="px-2.5 py-1 bg-orange-50 text-orange-600 text-xs font-medium rounded-lg hover:bg-orange-100">
                          Escalate
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" />
                No hits found
              </p>
            )}
          </SectionCard>
        );
      })}
    </div>
  );
};

// ============================================
// RISK TAB
// ============================================
const RiskTab = ({ riskAssessment }) => {
  if (!riskAssessment) {
    return <EmptyState icon={AlertTriangle} title="No risk assessment" description="Run a risk assessment for this case to view scoring details." action="New Assessment" />;
  }

  const ra = riskAssessment;

  return (
    <div className="space-y-5">
      {/* Overall */}
      <SectionCard title="Current Risk Assessment" icon={AlertTriangle}>
        <div className="flex items-center gap-6 mb-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">{ra.overall_score}</p>
            <p className="text-xs text-gray-500 mt-1">Overall Score</p>
          </div>
          <KycRiskBadge rating={ra.overall_rating} size="lg" />
        </div>

        {/* Factor Breakdown */}
        <div className="space-y-3 mb-4">
          <KycRiskBar label="Customer Risk" score={ra.customer_risk} />
          <KycRiskBar label="Country Risk" score={ra.country_risk} />
          <KycRiskBar label="Product Risk" score={ra.product_risk} />
          <KycRiskBar label="Channel Risk" score={ra.channel_risk} />
        </div>

        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">
          <RefreshCw className="w-3.5 h-3.5" />
          New Assessment
        </button>
      </SectionCard>

      {/* Individual Factors */}
      <SectionCard title="Individual Risk Factors" icon={BarChart3}>
        <div className="space-y-2">
          {ra.factors.map((f, idx) => (
            <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">{f.name}</span>
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-500 capitalize">{f.category}</span>
              </div>
              <span className={`text-sm font-bold ${f.score >= 7.5 ? 'text-red-600' : f.score >= 5 ? 'text-orange-600' : f.score >= 2.5 ? 'text-yellow-600' : 'text-green-600'}`}>
                {f.score.toFixed(1)}
              </span>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Risk History */}
      <SectionCard title="Risk History" icon={History}>
        {ra.history.length > 0 ? (
          <div className="space-y-2">
            {ra.history.map((h, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">{fmtDate(h.date)}</span>
                  <KycRiskBadge rating={h.rating} size="xs" />
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">{h.score.toFixed(1)}</span>
                  <span className="text-xs text-gray-400">by {h.assessor}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4">No previous assessments.</p>
        )}
      </SectionCard>

      {/* Override Section */}
      <SectionCard title="Risk Override" icon={Edit3}>
        <p className="text-xs text-gray-500 mb-3">Senior compliance officers can override the automated risk rating with justification.</p>
        <div className="flex items-center gap-3">
          <select className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Select override rating...</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="very_high">Very High</option>
          </select>
          <input type="text" placeholder="Justification..." className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700">
            Apply Override
          </button>
        </div>
      </SectionCard>
    </div>
  );
};

// ============================================
// SOURCE OF FUNDS TAB
// ============================================
const SourceOfFundsTab = ({ sourceOfFunds }) => {
  if (!sourceOfFunds) {
    return <EmptyState icon={DollarSign} title="No source of funds data" description="Add source of wealth and funds information for this case." action="Add Declaration" />;
  }

  const sof = sourceOfFunds;

  return (
    <div className="space-y-5">
      {/* Source of Wealth */}
      <SectionCard
        title="Source of Wealth"
        icon={Briefcase}
        headerAction={
          <button className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-100">
            <Plus className="w-3 h-3" /> Add
          </button>
        }
      >
        {sof.wealth_declarations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 pb-2">Type</th>
                  <th className="text-left text-xs font-semibold text-gray-500 pb-2">Description</th>
                  <th className="text-right text-xs font-semibold text-gray-500 pb-2">Value</th>
                  <th className="text-center text-xs font-semibold text-gray-500 pb-2">Evidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sof.wealth_declarations.map((w, idx) => (
                  <tr key={idx}>
                    <td className="py-2 font-medium text-gray-700">{w.type}</td>
                    <td className="py-2 text-gray-600">{w.description}</td>
                    <td className="py-2 text-right text-gray-700">${w.estimated_value?.toLocaleString()} {w.currency}</td>
                    <td className="py-2 text-center">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${w.evidence_status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {w.evidence_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-gray-400 text-center py-4">No wealth declarations.</p>
        )}
      </SectionCard>

      {/* Source of Funds */}
      <SectionCard
        title="Source of Funds"
        icon={DollarSign}
        headerAction={
          <button className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-100">
            <Plus className="w-3 h-3" /> Add
          </button>
        }
      >
        {sof.funds_entries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 pb-2">Source</th>
                  <th className="text-right text-xs font-semibold text-gray-500 pb-2">Amount</th>
                  <th className="text-left text-xs font-semibold text-gray-500 pb-2">Frequency</th>
                  <th className="text-left text-xs font-semibold text-gray-500 pb-2">Institution</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sof.funds_entries.map((f, idx) => (
                  <tr key={idx}>
                    <td className="py-2 font-medium text-gray-700">{f.source}</td>
                    <td className="py-2 text-right text-gray-700">${f.amount?.toLocaleString()} {f.currency}</td>
                    <td className="py-2 text-gray-600 capitalize">{f.frequency}</td>
                    <td className="py-2 text-gray-600">{f.originating_institution}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-gray-400 text-center py-4">No funds entries.</p>
        )}
      </SectionCard>

      {/* Expected Activity */}
      <SectionCard title="Expected Activity Profile" icon={Activity}>
        {sof.expected_activity ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            <InfoRow label="Annual Volume" value={`$${sof.expected_activity.annual_volume_usd?.toLocaleString()}`} />
            <InfoRow label="Max Transaction" value={`$${sof.expected_activity.max_transaction_usd?.toLocaleString()}`} />
            <InfoRow label="Frequency" value={sof.expected_activity.frequency} />
            <InfoRow label="Currencies" value={sof.expected_activity.currencies?.join(', ')} />
            <InfoRow label="Instruments" value={sof.expected_activity.instruments?.join(', ')} />
          </div>
        ) : (
          <p className="text-xs text-gray-400 text-center py-4">No expected activity defined.</p>
        )}
      </SectionCard>
    </div>
  );
};

// ============================================
// TAX TAB
// ============================================
const TaxTab = ({ tax }) => {
  if (!tax) {
    return <EmptyState icon={Scale} title="No tax information" description="Add tax IDs and classifications for this case." action="Add Tax ID" />;
  }

  return (
    <div className="space-y-5">
      {/* Tax IDs */}
      <SectionCard
        title="Tax Identification Numbers"
        icon={Scale}
        headerAction={
          <button className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-100">
            <Plus className="w-3 h-3" /> Add Tax ID
          </button>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 pb-2">Type</th>
                <th className="text-left text-xs font-semibold text-gray-500 pb-2">Number</th>
                <th className="text-left text-xs font-semibold text-gray-500 pb-2">Country</th>
                <th className="text-center text-xs font-semibold text-gray-500 pb-2">Validated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tax.tax_ids.map((t, idx) => (
                <tr key={idx}>
                  <td className="py-2 font-medium text-gray-700">{t.type}</td>
                  <td className="py-2 text-gray-700 font-mono text-xs">{t.number}</td>
                  <td className="py-2 text-gray-600">{t.country}</td>
                  <td className="py-2 text-center">
                    {t.validated ? (
                      <CheckCircle className="w-4 h-4 text-green-500 inline" />
                    ) : (
                      <Clock className="w-4 h-4 text-yellow-500 inline" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* FATCA */}
      <SectionCard title="FATCA Classification" icon={Flag}>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          <InfoRow label="Status" value={tax.fatca.status?.replace(/_/g, ' ')} />
          <InfoRow label="GIIN" value={tax.fatca.giin || 'N/A'} />
          <InfoRow label="W-Form Type" value={tax.fatca.w_form_type} />
          <InfoRow label="W-Form Date" value={fmtDate(tax.fatca.w_form_date)} />
        </div>
      </SectionCard>

      {/* CRS */}
      <SectionCard title="CRS Classification" icon={Globe}>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          <InfoRow label="Status" value={tax.crs.status?.replace(/_/g, ' ')} />
          <InfoRow label="Tax Residency" value={tax.crs.tax_residency_countries?.join(', ')} />
          <InfoRow label="Self-Cert Date" value={fmtDate(tax.crs.self_cert_date)} />
          <InfoRow label="Reportable" value={tax.reportable ? 'Yes' : 'No'} />
        </div>
      </SectionCard>
    </div>
  );
};

// ============================================
// MARKETS TAB
// ============================================
const MarketsTab = ({ caseMarkets }) => {
  const [expandedMarket, setExpandedMarket] = useState(null);

  if (caseMarkets.length === 0) {
    return <EmptyState icon={Globe} title="No markets" description="Add markets this investor will be onboarded to." action="Add Market" />;
  }

  const reqStatusIcon = {
    complete: <CheckCircle className="w-4 h-4 text-green-500" />,
    pending:  <Clock className="w-4 h-4 text-yellow-500" />,
    missing:  <XCircle className="w-4 h-4 text-red-400" />,
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-500">{caseMarkets.length} market{caseMarkets.length !== 1 ? 's' : ''}</p>
        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">
          <Plus className="w-3.5 h-3.5" />
          Add Market
        </button>
      </div>

      {caseMarkets.map((m) => {
        const isExpanded = expandedMarket === m.market_code;
        const pct = m.total > 0 ? Math.round((m.progress / m.total) * 100) : 0;
        return (
          <div key={m.market_code} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div
              className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setExpandedMarket(isExpanded ? null : m.market_code)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-900">{m.market_name}</span>
                  <span className="text-xs text-gray-500">({m.market_code})</span>
                </div>
                {/* Progress bar */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden max-w-xs">
                    <div
                      className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500">{m.progress}/{m.total}</span>
                </div>
              </div>
              <KycStatusBadge status={m.status === 'in_progress' ? 'in_review' : m.status === 'complete' ? 'approved' : m.status} size="sm" />
              <div className="flex-shrink-0">
                <span className={`text-xs font-medium ${m.csd_account_status === 'active' ? 'text-green-600' : 'text-yellow-600'}`}>
                  CSD: {m.csd_account_status}
                </span>
              </div>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </div>

            {isExpanded && (
              <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/50">
                <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wider">Requirements Checklist</h4>
                <div className="space-y-1.5">
                  {m.requirements.map((req, idx) => (
                    <div key={idx} className="flex items-center gap-2 py-1">
                      {reqStatusIcon[req.status] || reqStatusIcon.missing}
                      <span className={`text-sm ${req.status === 'complete' ? 'text-gray-700' : 'text-gray-500'}`}>{req.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// COUNTERPARTIES TAB
// ============================================
const CounterpartiesTab = ({ counterparties }) => {
  if (counterparties.length === 0) {
    return <EmptyState icon={Link2} title="No counterparties" description="Add counterparty KYC records for this case." action="Add Counterparty" />;
  }

  const ddStatusColors = {
    complete:    'bg-green-100 text-green-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    not_started: 'bg-gray-100 text-gray-500',
    overdue:     'bg-red-100 text-red-700',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500">{counterparties.length} counterpart{counterparties.length !== 1 ? 'ies' : 'y'}</p>
        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">
          <Plus className="w-3.5 h-3.5" />
          Add Counterparty
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Country</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">LEI</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Relationship</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">DD Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Risk</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {counterparties.map((cp) => (
                <tr key={cp.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{cp.name}</td>
                  <td className="px-4 py-3 text-gray-600 capitalize">{cp.type}</td>
                  <td className="px-4 py-3 text-gray-600">{cp.country}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">{cp.lei || '--'}</td>
                  <td className="px-4 py-3 text-gray-600 capitalize">{cp.relationship}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${ddStatusColors[cp.dd_status] || 'bg-gray-100 text-gray-500'}`}>
                      {cp.dd_status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <KycRiskBadge rating={cp.risk_rating} size="xs" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================================
// DOCUMENTS TAB
// ============================================
const DocumentsTab = ({ documents }) => {
  if (documents.length === 0) {
    return <EmptyState icon={FileText} title="No documents" description="Upload and manage documents for this case." action="Upload Document" />;
  }

  // Group by person_name
  const grouped = {};
  documents.forEach(doc => {
    const key = doc.person_name || 'General';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(doc);
  });

  const verificationColors = {
    verified: 'bg-green-100 text-green-700',
    pending:  'bg-yellow-100 text-yellow-700',
    rejected: 'bg-red-100 text-red-700',
  };

  const now = new Date();

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-500">{documents.length} document{documents.length !== 1 ? 's' : ''}</p>
        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">
          <Upload className="w-3.5 h-3.5" />
          Upload Document
        </button>
      </div>

      {Object.entries(grouped).map(([personName, docs]) => (
        <SectionCard key={personName} title={personName} icon={User}>
          <div className="space-y-2">
            {docs.map((doc) => {
              const isExpiringSoon = doc.expiry_date && new Date(doc.expiry_date) < new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000) && new Date(doc.expiry_date) > now;
              const isExpired = doc.expiry_date && new Date(doc.expiry_date) < now;
              return (
                <div key={doc.id} className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border ${isExpired ? 'border-red-200 bg-red-50/50' : isExpiringSoon ? 'border-amber-200 bg-amber-50/50' : 'border-gray-100'}`}>
                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}</span>
                      {doc.reused_from && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded">
                          <Copy className="w-2.5 h-2.5" />
                          Reused
                        </span>
                      )}
                      {isExpiringSoon && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded">
                          <AlertTriangle className="w-2.5 h-2.5" />
                          Expiring Soon
                        </span>
                      )}
                      {isExpired && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 rounded">
                          <XCircle className="w-2.5 h-2.5" />
                          Expired
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      {doc.doc_number && <span>#{doc.doc_number}</span>}
                      <span>{doc.issuing_country}</span>
                      <span>Issued: {fmtDate(doc.issue_date)}</span>
                      {doc.expiry_date && <span>Expires: {fmtDate(doc.expiry_date)}</span>}
                    </div>
                  </div>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${verificationColors[doc.verification_status] || 'bg-gray-100 text-gray-500'}`}>
                    {doc.verification_status?.replace(/_/g, ' ')}
                  </span>
                </div>
              );
            })}
          </div>
        </SectionCard>
      ))}
    </div>
  );
};

// ============================================
// MONITORING TAB
// ============================================
const MonitoringTab = ({ alerts }) => {
  const [alertFilter, setAlertFilter] = useState('all');

  const filteredAlerts = useMemo(() => {
    if (alertFilter === 'all') return alerts;
    return alerts.filter(a => a.severity === alertFilter);
  }, [alerts, alertFilter]);

  if (alerts.length === 0) {
    return <EmptyState icon={Bell} title="No alerts" description="No monitoring alerts for this case. This is a good sign!" />;
  }

  const severityColors = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high:     'bg-orange-100 text-orange-700 border-orange-200',
    medium:   'bg-yellow-100 text-yellow-700 border-yellow-200',
    low:      'bg-blue-100 text-blue-700 border-blue-200',
  };

  const typeColors = {
    document_expiry:    'bg-amber-100 text-amber-700',
    sanctions_hit:      'bg-red-100 text-red-700',
    pep_status_change:  'bg-orange-100 text-orange-700',
    adverse_media_hit:  'bg-purple-100 text-purple-700',
    case_sla_breach:    'bg-rose-100 text-rose-700',
  };

  const alertStatusColors = {
    open:          'bg-yellow-100 text-yellow-700',
    investigating: 'bg-blue-100 text-blue-700',
    acknowledged:  'bg-gray-100 text-gray-600',
    resolved:      'bg-green-100 text-green-700',
    dismissed:     'bg-gray-50 text-gray-400',
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        {['all', 'critical', 'high', 'medium', 'low'].map((f) => (
          <button
            key={f}
            onClick={() => setAlertFilter(f)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${alertFilter === f ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
          >
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filteredAlerts.map((alert) => (
          <div key={alert.id} className={`bg-white rounded-xl shadow-sm border p-4 ${severityColors[alert.severity]?.split(' ')[2] ? `border-l-4 ${severityColors[alert.severity].split(' ')[2]}` : 'border-gray-100'}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded ${typeColors[alert.type] || 'bg-gray-100 text-gray-600'}`}>
                  {ALERT_TYPE_LABELS[alert.type] || alert.type}
                </span>
                <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded ${severityColors[alert.severity]?.split(' ').slice(0, 2).join(' ') || 'bg-gray-100 text-gray-600'}`}>
                  {alert.severity?.toUpperCase()}
                </span>
              </div>
              <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full ${alertStatusColors[alert.status] || 'bg-gray-100 text-gray-500'}`}>
                {alert.status?.replace(/_/g, ' ')}
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1">{alert.title}</p>
            <p className="text-xs text-gray-600 mb-3">{alert.description}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span>{fmtDateTime(alert.created_at)}</span>
                {alert.assigned_to && <span>Assigned: {alert.assigned_to}</span>}
              </div>
              <div className="flex items-center gap-1.5">
                <button className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100">Acknowledge</button>
                <button className="px-2 py-1 text-xs font-medium text-amber-600 bg-amber-50 rounded hover:bg-amber-100">Investigate</button>
                <button className="px-2 py-1 text-xs font-medium text-green-600 bg-green-50 rounded hover:bg-green-100">Resolve</button>
                <button className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-50 rounded hover:bg-gray-100">Dismiss</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// CHANGES TAB
// ============================================
const ChangesTab = ({ changes }) => {
  if (changes.length === 0) {
    return <EmptyState icon={History} title="No change requests" description="Submit a change request to modify case data." action="Submit Change Request" />;
  }

  const changeStatusColors = {
    pending:   'bg-yellow-100 text-yellow-700',
    approved:  'bg-green-100 text-green-700',
    completed: 'bg-green-100 text-green-700',
    rejected:  'bg-red-100 text-red-700',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{changes.length} change request{changes.length !== 1 ? 's' : ''}</p>
        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">
          <Plus className="w-3.5 h-3.5" />
          Submit Change Request
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Summary</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Requested By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {changes.map((ch) => (
                <tr key={ch.id} className={`hover:bg-gray-50 ${ch.status === 'pending' ? 'bg-yellow-50/30' : ''}`}>
                  <td className="px-4 py-3 font-medium text-gray-700">{ch.request_type}</td>
                  <td className="px-4 py-3 text-gray-600">{ch.summary}</td>
                  <td className="px-4 py-3 text-gray-600">{ch.requested_by}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${changeStatusColors[ch.status] || 'bg-gray-100 text-gray-500'}`}>
                      {ch.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(ch.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN CASE DETAIL PAGE
// ============================================
const KycCaseDetailPage = () => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const {
    getCaseById, getPersons, getOwnership, getDocuments, getRiskAssessment,
    getSourceOfFunds, getTax, getCaseMarkets, getCounterparties, getAlerts,
    getChanges, getAuditLog, getScreeningHitsForCase,
  } = useKycMockData();

  const caseData = getCaseById(caseId);
  const persons = getPersons(caseId);
  const ownership = getOwnership(caseId);
  const documents = getDocuments(caseId);
  const riskAssessment = getRiskAssessment(caseId);
  const sourceOfFunds = getSourceOfFunds(caseId);
  const tax = getTax(caseId);
  const caseMarkets = getCaseMarkets(caseId);
  const counterparties = getCounterparties(caseId);
  const alerts = getAlerts(caseId);
  const changes = getChanges(caseId);
  const auditLog = getAuditLog(caseId);
  const screeningHits = getScreeningHitsForCase(caseId);

  const userRole = 'bank_compliance_maker';
  const availableTransitions = caseData ? getAvailableTransitions(caseData.status, userRole) : [];

  if (!caseData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-700 mb-2">Case Not Found</h2>
            <p className="text-sm text-gray-500 mb-4">The case "{caseId}" could not be found.</p>
            <button
              onClick={() => navigate('/kyc')}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Action buttons based on available transitions
  const actionButtons = [];
  if (availableTransitions.includes('submitted')) {
    actionButtons.push({ label: 'Submit', color: 'bg-blue-600 hover:bg-blue-700', targetState: 'submitted' });
  }
  if (availableTransitions.includes('in_review')) {
    actionButtons.push({ label: 'Begin Review', color: 'bg-indigo-600 hover:bg-indigo-700', targetState: 'in_review' });
  }
  if (availableTransitions.includes('screening')) {
    actionButtons.push({ label: 'Start Screening', color: 'bg-purple-600 hover:bg-purple-700', targetState: 'screening' });
  }
  if (availableTransitions.includes('risk_assessment')) {
    actionButtons.push({ label: 'Assess Risk', color: 'bg-orange-600 hover:bg-orange-700', targetState: 'risk_assessment' });
  }
  if (availableTransitions.includes('pending_approval')) {
    actionButtons.push({ label: 'Submit for Approval', color: 'bg-amber-600 hover:bg-amber-700', targetState: 'pending_approval' });
  }
  if (availableTransitions.includes('approved')) {
    actionButtons.push({ label: 'Approve', color: 'bg-green-600 hover:bg-green-700', targetState: 'approved' });
  }
  if (availableTransitions.includes('rejected')) {
    actionButtons.push({ label: 'Reject', color: 'bg-red-600 hover:bg-red-700', targetState: 'rejected' });
  }
  if (availableTransitions.includes('information_requested')) {
    actionButtons.push({ label: 'Request Info', color: 'bg-sky-600 hover:bg-sky-700', targetState: 'information_requested' });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb & Back */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <button onClick={() => navigate('/kyc')} className="hover:text-gray-700 flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" />
            KYC Dashboard
          </button>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-900 font-medium">{caseData.case_ref}</span>
        </div>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-xl font-bold text-gray-900">{caseData.case_ref}</h1>
                <KycStatusBadge status={caseData.status} size="md" />
                {caseData.risk_rating && (
                  <KycRiskBadge rating={caseData.risk_rating} score={caseData.risk_score} showScore size="md" />
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1.5">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  {caseData.investor_name}
                </span>
                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                  {CASE_TYPE_LABELS[caseData.case_type] || caseData.case_type}
                </span>
                {caseData.assigned_analyst && (
                  <span className="flex items-center gap-1.5">
                    <User className="w-4 h-4 text-gray-400" />
                    {caseData.assigned_analyst}
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              {actionButtons.map((btn, idx) => (
                <button
                  key={idx}
                  className={`px-3 py-1.5 text-white text-xs font-medium rounded-lg transition-colors ${btn.color}`}
                  title={`Transition to ${STATE_LABELS[btn.targetState] || btn.targetState}`}
                >
                  {btn.label}
                </button>
              ))}
              <button className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200">
                Assign
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-5">
          <nav className="flex space-x-1 -mb-px overflow-x-auto">
            {DETAIL_TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="pb-10">
          {activeTab === 'overview' && <OverviewTab caseData={caseData} auditLog={auditLog} />}
          {activeTab === 'persons' && <PersonsTab persons={persons} />}
          {activeTab === 'ownership' && <OwnershipTab ownership={ownership} />}
          {activeTab === 'screening' && <ScreeningTab persons={persons} screeningHits={screeningHits} />}
          {activeTab === 'risk' && <RiskTab riskAssessment={riskAssessment} />}
          {activeTab === 'source_of_funds' && <SourceOfFundsTab sourceOfFunds={sourceOfFunds} />}
          {activeTab === 'tax' && <TaxTab tax={tax} />}
          {activeTab === 'markets' && <MarketsTab caseMarkets={caseMarkets} />}
          {activeTab === 'counterparties' && <CounterpartiesTab counterparties={counterparties} />}
          {activeTab === 'documents' && <DocumentsTab documents={documents} />}
          {activeTab === 'monitoring' && <MonitoringTab alerts={alerts} />}
          {activeTab === 'changes' && <ChangesTab changes={changes} />}
        </div>
      </div>
    </div>
  );
};

export default KycCaseDetailPage;
